/* oxlint-disable no-underscore-dangle*/
/**
 * Config resolved at RUNTIME (build-once → promote dev→staging→prod strategy).
 *
 * - Web/PWA: the container injects `window.__APP_CONFIG__` via `/config.js` (generated
 *   by the nginx entrypoint from environment variables at startup). One image
 *   serves every environment; only the container's env changes.
 * - Tauri (desktop/mobile): there is no `/config.js`; it falls back to values baked
 *   into `import.meta.env.VITE_*` (build-time) → unchanged behavior.
 * - Local dev: same (import.meta.env from apps/web/.env) or localhost fallback.
 */
interface AppRuntimeConfig {
  SERVER_URL?: string;
  POWERSYNC_URL?: string;
}

const runtime: AppRuntimeConfig =
  (globalThis as typeof globalThis & { __APP_CONFIG__?: AppRuntimeConfig }).__APP_CONFIG__ ?? {};

/**
 * First ACTUALLY present value. Plain `??` is not enough: the entrypoint
 * writes `"KEY": ""` for every env the container does not define, and an empty
 * string is not nullish → it would win over the default and leave, for example, a base
 * URL empty. Here "unset" and "empty" mean the same thing.
 */
function firstConfigured(...values: (string | undefined)[]): string | undefined {
  return values.find((v) => v !== undefined && v.trim() !== "")?.trim();
}

/** Backend base (auth/api/server). */
export const SERVER_URL: string =
  firstConfigured(
    runtime.SERVER_URL,
    import.meta.env.VITE_SERVER_URL,
    import.meta.env.VITE_API_URL,
  ) ?? "http://localhost:3333";

/** PowerSync service endpoint. */
export const POWERSYNC_URL: string =
  firstConfigured(runtime.POWERSYNC_URL, import.meta.env.VITE_POWERSYNC_URL) ??
  "http://localhost:8085";

/**
 * PUBLIC origin of the web/PWA (`app.chronostracker.com` in prod). Only
 * `lib/legal-links.ts` uses it to build an absolute URL when a legal link
 * opens from NATIVE: inside Tauri's WebView the origin is `tauri://localhost`,
 * which the system browser cannot resolve. Build-time only (as documented in
 * the file header, Tauri has no `/config.js`) — it does not need to react
 * at runtime because the legal content is the same in every environment
 * and only the store build (prod) matters for this link.
 */
export const WEB_URL: string =
  firstConfigured(import.meta.env.VITE_WEB_URL) ?? "https://app.chronostracker.com";
